import Phaser from "phaser";
import type { ArmorItemData } from "../models/common";

export type { ArmorItemData };

const HEALTH_BAR_DEPTH = 1001;
const CIRCLE_RADIUS = 4; // display radius in pixels
const BAR_WIDTH = 40; // must match health bar width
const FULL_WIDTH_COUNT = 4; // number of dots that fill the full bar width
// Fixed step so that FULL_WIDTH_COUNT dots span exactly BAR_WIDTH
const CIRCLE_STEP = (BAR_WIDTH - CIRCLE_RADIUS * 2) / (FULL_WIDTH_COUNT - 1);
const MAX_SHIELDS = 10; // maximum armor items to display

function hexStringToInt(hex: string): number {
  return parseInt(hex.replace("#", ""), 16);
}

/**
 * Renders armor as a row of filled circles — one per equipped armor item.
 * Each circle's fill opacity reflects that item's current / max armor ratio.
 * The circle color comes from the item's stats.color property.
 */
export class ArmorShieldBar {
  private scene: Phaser.Scene;
  private x: number;
  private y: number;
  private armorItems: ArmorItemData[] = [];
  private _visible: boolean = false;
  private readonly scale: number;
  private readonly outline: boolean;

  public bg: Phaser.GameObjects.Graphics;
  /** Kept as empty array for compatibility with container spread in player.ts */
  public sprites: Phaser.GameObjects.Sprite[] = [];

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    hiddenByDefault: boolean = true,
    scale: number = 1,
    outline: boolean = false,
  ) {
    this.scene = scene;
    this.x = x;
    this.y = y;
    this.scale = scale;
    this.outline = outline;

    this.bg = scene.add.graphics();
    this.bg.setDepth(HEALTH_BAR_DEPTH);

    if (hiddenByDefault) {
      this.hide();
    }
  }

  setArmorItems(items: ArmorItemData[]) {
    this.armorItems = items;
    this.redraw();
  }

  private redraw() {
    this.bg.clear();
    if (!this._visible) return;

    const r = CIRCLE_RADIUS * this.scale;
    const step = CIRCLE_STEP * this.scale;
    const barWidth = BAR_WIDTH * this.scale;

    const n = Math.min(this.armorItems.length, MAX_SHIELDS);

    // Fixed spacing so 4 dots fill the full bar width.
    // The group is centred horizontally within the bar regardless of count.
    const groupSpan = (n - 1) * step;
    const barCenterX = this.x + barWidth / 2;
    const startCx = barCenterX - groupSpan / 2;
    const cy = this.y + r;

    for (let i = 0; i < n; i++) {
      const item = this.armorItems[i];
      const fillRatio =
        item.max > 0 ? Phaser.Math.Clamp(item.current / item.max, 0, 1) : 0;

      const cx = startCx + i * step;

      // White outline ring drawn behind the dark background
      if (this.outline) {
        this.bg.fillStyle(0xffffff, 1);
        this.bg.fillCircle(cx, cy, r + 1.5);
      }

      // Dark background circle (always visible — shows the slot)
      this.bg.fillStyle(0x222222, 1);
      this.bg.fillCircle(cx, cy, r);

      // Colored fill circle, alpha reflects current armor ratio
      if (fillRatio > 0) {
        const colorInt = hexStringToInt(item.color ?? "#888888");
        this.bg.fillStyle(colorInt, fillRatio);
        this.bg.fillCircle(cx, cy, r);
      }
    }
  }

  show(_hideAfterMs?: number) {
    this._visible = true;
    this.bg.setVisible(true);
    this.redraw();
  }

  hide() {
    this._visible = false;
    this.bg.setVisible(false);
  }

  destroy() {
    this.bg.destroy();
  }
}
